import { SelectedSlotRow } from '@frontend/components/Calendar/BookingsListView/CalendarSlotTable/hooks'
import { Dayjs, dayjs } from '@frontend/lib/dayjs-plugins'
import {
  CalendarBookingStoreData,
  CalendarSelectedShipment,
  CalendarViewMode,
  SlotRowSelectionType,
} from '@frontend-types/forms'
import { registerInDevtools, Store } from 'pullstate'

import { InboundOutboundType, SlotState } from '@shared/const'

import { User } from '@shared-types/domain'
import { Identifier, Nullable } from '@shared-types/util'

import {
  closeSideColumn,
  openBookShipmentColumn,
  openCreateSlotColumn,
  openEditSlotColumn,
} from './GlobalSideColumnStore'

const initialBookingStore: CalendarBookingStoreData = {
  isBookingDetailsPopupOpen: false,
  selectedBookingId: '',
  initialSelectedShipment: null,
  showCreateShipmentForm: false,
  filters: {
    start_time: dayjs.utc().startOf('day').toISOString(),
    end_time: dayjs.utc().endOf('day').toISOString(),
    state: SlotState.ACTIVE,
    type: InboundOutboundType.INBOUND,
  },
  facility: null,
  viewMode: 'list',
  selectedDate: dayjs.utc(),
  selectedMonth: dayjs.utc(),
  initialPurchaseOrderId: null,
  searchTerm: '',
  selectedSlotRows: [],
  selectedSlotRowIds: [],
  slotSelectionType: SlotRowSelectionType.ALL,
}

const CalendarBookingStore = new Store(initialBookingStore)

// Existing Dayjs instances keep the locale they were created with, so switching the app locale
// must re-apply it explicitly to dates already held in the store.
export function setStoreLocale(locale: string) {
  CalendarBookingStore.update((s) => {
    s.selectedDate = s.selectedDate.locale(locale)
    s.selectedMonth = s.selectedMonth.locale(locale)
  })
}

export function setInitialSelectedShipment(selectedShipment: CalendarSelectedShipment) {
  CalendarBookingStore.update((s) => {
    s.initialSelectedShipment = selectedShipment
  })
}

export function setSlotSelectionType(slotSelectionType: SlotRowSelectionType) {
  CalendarBookingStore.update((s) => {
    s.slotSelectionType = slotSelectionType
  })
}

export function setSelectedSlotRows(selectedSlotRows: SelectedSlotRow[]) {
  CalendarBookingStore.update((s) => {
    s.selectedSlotRows = selectedSlotRows
  })
}

export function setSelectedSlotRowIds(selectedSlotRowIds: string[]) {
  CalendarBookingStore.update((s) => {
    s.selectedSlotRowIds = selectedSlotRowIds
  })
}

export function resetSelectedSlotRows() {
  CalendarBookingStore.update((s) => {
    s.selectedSlotRows = []
    s.selectedSlotRowIds = []
    s.slotSelectionType = SlotRowSelectionType.ALL
  })
}

export function closeAllBookingDetailViews() {
  CalendarBookingStore.update((s) => {
    s.isBookingDetailsPopupOpen = false
    s.selectedBookingId = ''
    s.initialSelectedShipment = null
    s.showCreateShipmentForm = false
    s.initialPurchaseOrderId = null
  })
  closeSideColumn()
}

export function openCreateSlotSider({
  facilityId,
  initialSelectedShipment = null,
}: {
  facilityId: string
  initialSelectedShipment?: CalendarSelectedShipment
}) {
  closeAllBookingDetailViews()
  CalendarBookingStore.update((s) => {
    s.initialSelectedShipment = initialSelectedShipment
  })
  openCreateSlotColumn(facilityId)
}

type OpenBookShipmentSiderParams = {
  bookingId?: string
  initialSelectedShipment?: CalendarSelectedShipment
  showCreateShipmentForm?: boolean
  purchaseOrderId?: Nullable<string>
}

export function openBookShipmentSider({
  bookingId,
  initialSelectedShipment,
  showCreateShipmentForm,
  purchaseOrderId,
}: OpenBookShipmentSiderParams) {
  CalendarBookingStore.update((s) => {
    if (bookingId !== undefined) {
      s.selectedBookingId = bookingId
    }

    if (initialSelectedShipment !== undefined) {
      s.initialSelectedShipment = initialSelectedShipment
    }

    if (showCreateShipmentForm !== undefined) {
      s.showCreateShipmentForm = showCreateShipmentForm
    }

    s.initialPurchaseOrderId = purchaseOrderId || null
  })

  openBookShipmentColumn({
    selectedShipmentNid: initialSelectedShipment?.nid,
    selectedPOId: purchaseOrderId,
    showCreateShipmentForm,
  })
}

export function openBookingDetailsPopup(bookingId: Identifier) {
  closeAllBookingDetailViews()
  CalendarBookingStore.update((s) => {
    s.isBookingDetailsPopupOpen = true
    s.selectedBookingId = bookingId
  })
}

export function openEditSlotSider(
  facilityId: Identifier,
  bookingId: Identifier,
  shipmentType: InboundOutboundType
) {
  CalendarBookingStore.update((s) => {
    s.selectedBookingId = bookingId
  })

  openEditSlotColumn(facilityId, bookingId, shipmentType)
}

export function setViewMode(viewMode: CalendarViewMode) {
  CalendarBookingStore.update((s) => {
    s.viewMode = viewMode

    if (viewMode === 'list') {
      s.selectedMonth = s.selectedDate.clone().startOf('month')
    }
  })
}

export function setMainCalendarCurrentDate(newDate: Dayjs) {
  const newDateUtc = newDate.clone().utc()
  CalendarBookingStore.update((s) => {
    s.selectedDate = newDateUtc
    s.selectedMonth = s.selectedDate.clone().startOf('month')
    s.filters.start_time = newDateUtc.startOf('day').toISOString()
    s.filters.end_time = newDateUtc.endOf('day').toISOString()
  })
}

export function setSelectedMonth(monthDate: Dayjs) {
  CalendarBookingStore.update((s) => {
    s.selectedMonth = monthDate.clone().startOf('month')
  })
}

export function switchToArchivedSlotsView() {
  CalendarBookingStore.update((s) => {
    s.filters.state = SlotState.INACTIVE
  })
  setViewMode('list')
}

export function switchToActiveSlotsView() {
  CalendarBookingStore.update((s) => {
    s.filters.state = SlotState.ACTIVE
  })
}

export function selectBookingInCalendar(bookingId: string) {
  CalendarBookingStore.update((s) => {
    s.selectedBookingId = bookingId
  })

  openBookShipmentColumn({
    selectedSlotId: bookingId,
  })
}

export function setSearchTerm(currentUser: User, searchTerm: string) {
  CalendarBookingStore.update((s) => {
    s.searchTerm = searchTerm
    s.filters.q = searchTerm
  })
}

registerInDevtools({ CalendarBookingStore }, { namespace: 'CalendarBookingStore' })

export default CalendarBookingStore
